import {
  AuthenticationProvider,
  AuthenticationProviderOptions,
} from '@microsoft/microsoft-graph-client';
import { currentAccount, getMsalApp, scopes as defaultScopes } from './oneDriveAuth';

/**
 * Hands the Graph client an access token, and never does anything else.
 *
 * That restraint is the whole point. What used to be here was:
 *
 *     try   { return (await acquireTokenSilent(...)).accessToken }
 *     catch { await loginPopup(...); ... acquireTokenPopup(...) }
 *
 * - an interactive sign-in started from inside a token request, which is to
 * say from inside whatever Graph call happened to need a token, which is to
 * say from no user gesture at all. On iOS Safari that popup is blocked; on a
 * home-screen PWA it could not have delivered a result even if it opened. And
 * because msal v1's silent path relied on a hidden iframe that ITP blocks, the
 * catch block ran *every time*, so this was the normal path on a phone rather
 * than the exceptional one.
 *
 * Now it fails instead, with something the caller can recognise. Interaction
 * is a decision for a component with a click in hand - oneDriveService's
 * authorize() - not for a middleware three layers below one.
 */

/**
 * Thrown when there is no token to be had without asking the user.
 *
 * Deliberately its own type: oneDriveService turns this into "please sign in"
 * rather than into a snackbar full of Microsoft error text, and the storage
 * provider needs to tell it apart from OneDrive being down.
 */
export class OneDriveInteractionRequiredError extends Error {
  constructor(message?: string) {
    super(message || 'OneDrive needs you to sign in again.');
    this.name = 'OneDriveInteractionRequiredError';
  }
}

/**
 * @class
 * Class representing MSALAuthenticationProvider
 * @implements AuthenticationProvider
 */
export class MSALAuthenticationProvider implements AuthenticationProvider {
  scopes: string[];

  /**
   * @public
   * @constructor
   * @param {string[]} [scopes] - An array of graph scopes
   */
  constructor(scopes?: string[]) {
    this.scopes = scopes && scopes.length ? scopes : defaultScopes;
  }

  /**
   * @public
   * @async
   * To get the access token
   * @param {AuthenticationProviderOptions} authenticationProviderOptions - The authentication provider options object
   * @returns The promise that resolves to an access token
   */
  async getAccessToken(authenticationProviderOptions?: AuthenticationProviderOptions) {
    const requested = authenticationProviderOptions && authenticationProviderOptions.scopes;
    const scopes = requested && requested.length ? requested : this.scopes;

    if (scopes.length === 0) {
      const error = new Error();
      error.name = 'EmptyScopes';
      error.message = 'Scopes cannot be empty, Please provide a scope';
      throw error;
    }

    const app = await getMsalApp();
    const account = currentAccount(app);

    if (!account) {
      throw new OneDriveInteractionRequiredError();
    }

    try {
      const authResponse = await app.acquireTokenSilent({ scopes, account });

      return authResponse.accessToken;
    } catch (err) {
      // Every reason this can fail - an expired refresh token, a revoked
      // grant, a newly required consent - has the same remedy, and it is not
      // one this code can apply. Say so and let a button deal with it.
      throw new OneDriveInteractionRequiredError((err as Error).message || undefined);
    }
  }

  /**
   * @public
   * To add the scopes to the existing set of scopes
   * @param {string[]} scopes - The array of graph scope values
   * @returns Nothing
   */
  addScopes(scopes: string[]) {
    if (scopes.length === 0) {
      const error = new Error();
      error.name = 'EmptyScopes';
      error.message = 'Scopes array cannot be empty';
      throw error;
    }
    this.scopes = Array.from(new Set(this.scopes.concat(scopes)));
  }
  /**
   * @public
   * To clear the graph scopes
   * @returns Nothing
   */
  clearScopes() {
    this.scopes = [];
  }
}
